import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { CheckCircle, DeviceMobile, Bank } from "@phosphor-icons/react";
import { CONTACT } from "@/lib/contact";

const FEATURE_KEYS = {
  start: ["start_1", "start_2", "start_3", "start_4"],
  evolution: ["evolution_1", "evolution_2", "evolution_3", "evolution_4", "evolution_5"],
  elite: ["elite_1", "elite_2", "elite_3", "elite_4", "elite_5", "elite_6"],
};

export default function Plans() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data));
  }, []);

  return (
    <Layout>
      <section className="border-b border-as-border bg-as-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="as-label text-as-green mb-3">{t("plans_page.eyebrow_module")}</p>
          <h1 className="as-heading text-5xl text-white">
            {t("plans_page.title_1")} <span className="text-as-green">{t("plans_page.title_2")}</span>
          </h1>
          <p className="text-as-gray mt-4 max-w-xl">
            {t("plans_page.subtitle")}
          </p>
        </div>
      </section>

      <section className="bg-as-surface py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                featureKeys={FEATURE_KEYS[p.id] || []}
                popular={p.id === "evolution"}
                onSelect={() => setSelectedPlan(p)}
              />
            ))}
          </div>

          {selectedPlan && (
            <div className="mt-16 max-w-2xl mx-auto as-card !p-8" data-testid="payment-options">
              <p className="as-label text-as-green mb-3">{t("plans_page.pay_label")}</p>
              <h2 className="as-heading text-3xl text-white mb-2">{selectedPlan.name}</h2>
              <p className="as-heading text-4xl text-as-green mb-6">${selectedPlan.amount} USD</p>

              <p className="as-label mb-4">{t("plans_page.methods_label")}</p>
              <div className="space-y-3">
                <a
                  href={`https://wa.me/${CONTACT.phoneIntl}?text=${encodeURIComponent(t("plans_page.wa_zelle_message", { plan: selectedPlan.name, amount: selectedPlan.amount }))}`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full as-card hover:border-as-green flex items-center gap-4 !p-5 transition-all"
                  data-testid="pay-zelle-btn"
                >
                  <Bank size={32} weight="duotone" color="#39FF14" />
                  <div className="flex-1">
                    <p className="text-white font-bold">Zelle</p>
                    <p className="text-xs text-as-gray">{t("plans_page.zelle_desc")}</p>
                  </div>
                  <span className="as-label text-as-green">{t("plans_page.contact")}</span>
                </a>

                <a
                  href={`https://wa.me/${CONTACT.phoneIntl}?text=${encodeURIComponent(t("plans_page.wa_venmo_message", { plan: selectedPlan.name, amount: selectedPlan.amount }))}`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full as-card hover:border-as-green flex items-center gap-4 !p-5 transition-all"
                  data-testid="pay-venmo-btn"
                >
                  <DeviceMobile size={32} weight="duotone" color="#39FF14" />
                  <div className="flex-1">
                    <p className="text-white font-bold">Venmo</p>
                    <p className="text-xs text-as-gray">{t("plans_page.venmo_desc")}</p>
                  </div>
                  <span className="as-label text-as-green">{t("plans_page.contact")}</span>
                </a>
              </div>
              <p className="text-xs text-as-gray-dim mt-6 leading-relaxed">
                {t("plans_page.payment_note")}
              </p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}

const PlanCard = ({ plan, featureKeys, popular, onSelect }) => {
  const { t } = useTranslation();
  return (
    <div
      className={
        popular
          ? "bg-as-black border-2 border-as-green p-8 flex flex-col relative glow-green"
          : "bg-as-surface border border-as-border p-8 flex flex-col hover:border-as-green transition-colors"
      }
      data-testid={`plan-${plan.id}`}
    >
      {popular && (
        <span className="absolute -top-3 left-8 bg-as-green text-as-black px-3 py-1 as-label text-[10px]">
          {t("plans_page.popular")}
        </span>
      )}
      <p className="as-heading text-2xl text-white mb-2">{plan.name}</p>
      <p className="as-heading text-5xl text-as-green mb-1">${plan.amount}</p>
      <p className="as-label mb-6">{t("plans_page.monthly_label")}</p>
      <ul className="space-y-3 mb-8 flex-1">
        {featureKeys.map((k) => (
          <li key={k} className="flex items-start gap-2 text-sm text-white">
            <CheckCircle size={18} weight="fill" color="#39FF14" className="flex-shrink-0 mt-0.5" />
            <span>{t(`plan_features.${k}`)}</span>
          </li>
        ))}
      </ul>
      <button onClick={onSelect} className={popular ? "as-btn-primary w-full" : "as-btn-secondary w-full"} data-testid={`select-${plan.id}`}>
        {t("plans_page.choose")}
      </button>
    </div>
  );
};
